import type { ComponentDocs } from '../component-docs.js';

/**
 * What `BaseField` says about itself.
 *
 * IT REGISTERS NO ELEMENT. There is no `<caos-base-field>` and there never will be: it is the
 * abstract class the single-line inputs extend, so a subclass differs from its siblings by the one
 * word it passes to `super()` — the inner input's type — and by nothing else. That is the closed-
 * catalogue rule holding: two fields that differ by props are two configurations of one piece, not
 * two pieces. `placements` below is therefore `[]` — it says NOWHERE, which is a different and
 * stronger statement than saying nothing.
 *
 * IT IS DOCUMENTED ANYWAY, because it is where the shared contract actually lives. Everything
 * below is what TextField, NumberField and DateField each have, and the reason they have it. The
 * examples show it as the three primitives that ARE it — the only honest way to show a piece that
 * cannot be put on a page on its own.
 *
 * WHAT IS SHARED, PRECISELY. One template — a label, an input, a message — one stylesheet, one
 * generated id joining the label to the control, one pair of re-dispatched events, one `focus()`
 * that selects what is already there, and one `sync()` that forwards every observed attribute to
 * the inner input. `min`, `max` and `step` are forwarded by that shared `sync()` regardless of
 * subclass, so they are listed here as part of the contract even though a text input ignores all
 * three.
 *
 * WHAT IS NOT SHARED. CheckboxField, PicklistField and LookupField are NOT built on this. They
 * look like these fields and behave like them, but each carries its own template and its own copy
 * of the label/message/invalid treatment. That duplication is a fact about the library rather than
 * something to imply away by listing them here.
 */
const docs: ComponentDocs = {
  aliases: ['field base', 'input base class', 'shared field', 'form control base'],

  answers: [
    'find out what every single-line field takes',
    'understand why the text, number and date fields look identical',
    'style the label, the box or the message of any of them at once',
    'add another single-line input to the library',
  ],

  // Nowhere. It registers no element, so there is nothing to place.
  placements: [],

  inputs: [
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description:
        "The field's name, drawn above the control. The label carries a `for` pointing at a generated input " +
        'id, so clicking it focuses the control. Left unset, the label collapses and nothing is drawn.',
    },
    {
      name: 'placeholder',
      attribute: 'placeholder',
      type: 'text',
      description: 'Forwarded to the inner input as its placeholder, and removed again when the attribute goes.',
    },
    {
      name: 'value',
      attribute: 'value',
      property: true,
      type: 'text',
      readsBack:
        'the live contents of the inner control — what the person has typed. Writing goes the other way, to the ' +
        'attribute, which the control then follows. The two agree because the shared implementation writes the ' +
        'attribute back on every keystroke; they are not the same thing being read and written, which is worth ' +
        'knowing for anything that sets the attribute and reads the property in the same breath.',
      description:
        'The current value. It is two-way: the shared implementation writes it back on every keystroke, so the ' +
        'attribute and the control never disagree.',
    },
    {
      name: 'name',
      attribute: 'name',
      type: 'text',
      description: 'The form name forwarded to the inner input.',
    },
    {
      name: 'min',
      attribute: 'min',
      type: 'text',
      description:
        'Forwarded to the inner input. Meaningful where the input type has a floor — a number or a date — and ' +
        'ignored by a text input.',
    },
    {
      name: 'max',
      attribute: 'max',
      type: 'text',
      description: 'Forwarded to the inner input. Meaningful for a number or a date; ignored by a text input.',
    },
    {
      name: 'step',
      attribute: 'step',
      type: 'text',
      description: 'Forwarded to the inner input. The increment for a number, the day interval for a date; ignored by a text input.',
    },
    {
      name: 'required',
      attribute: 'required',
      type: 'boolean',
      description:
        'Draws the asterisk beside the label — marked hidden from a screen reader, because the input already ' +
        'reports required itself — and sets `required` on the inner input.',
      default: false,
    },
    {
      name: 'disabled',
      attribute: 'disabled',
      type: 'boolean',
      description: 'Sets `disabled` on the inner input: greyed, not focusable, not submitted.',
      default: false,
    },
    {
      name: 'invalid',
      attribute: 'invalid',
      type: 'boolean',
      description:
        'Turns the outline and the message red and sets `aria-invalid` on the input. Nothing in the shared ' +
        'implementation decides validity; it only draws the state it is given.',
      default: false,
    },
    {
      name: 'message',
      attribute: 'message',
      type: 'text',
      description:
        'The line under the control. Muted on its own, red when `invalid` is also set. Empty collapses, so an ' +
        'unset message takes no space.',
    },
    {
      name: 'inline',
      attribute: 'inline',
      type: 'boolean',
      description:
        'Switch from the boxed form rendering to the record-detail one: no box, an accent underline, inheriting ' +
        'the surrounding font and size, with the label and the message hidden. Honoured through the stylesheet ' +
        'rather than through the attribute callback, so it is not in `observedAttributes` — it works all the same.',
      default: false,
    },
    {
      name: 'numeric',
      attribute: 'numeric',
      type: 'boolean',
      description:
        'Draw the value in tabular figures so a column of them lines up. NumberField sets this on itself when it ' +
        'connects; the others do not, and there is rarely a reason to set it by hand.',
      default: false,
    },
  ],

  // Both are plain DOM events rather than CustomEvents. They are re-dispatched from inside the
  // shadow root with `composed: true` so they cross the boundary — without that, a page listening
  // on the element would never hear the inner input.
  events: [
    { name: 'input', when: 'On every keystroke, after the value attribute has been written back.', carries: 'nothing' },
    { name: 'change', when: 'When the inner input commits — focus leaves, Enter, or a picker is used.', carries: 'nothing' },
  ],

  // The template is built in the constructor and holds no <slot>. Nothing goes inside these fields.
  // Nothing to read back that cannot also be set. `value` can be read off the element, but a page can
  // set it too, so it is documented as an input rather than here.
  state: [],

  slots: [],

  tokens: [
    { token: '--caos-font', controls: 'The typeface of the label, the control and the message.' },
    { token: '--caos-text-body', controls: 'The size of the label and of the value.' },
    { token: '--caos-space-1', controls: 'The gap between the label, the control and the message.' },
    { token: '--caos-space-3', controls: 'The vertical padding inside the control.' },
    { token: '--caos-radius', controls: 'The corner radius of the control.' },
    { token: '--caos-color-border', controls: 'The outline at rest.' },
    { token: '--caos-color-border-strong', controls: 'The outline while the control has focus.' },
    { token: '--caos-color-surface-2', controls: 'The fill of the control, at rest and disabled.' },
    { token: '--caos-color-text', controls: 'The colour of the value.' },
    { token: '--caos-color-text-muted', controls: 'The label, the placeholder, the message and disabled text.' },
    { token: '--caos-color-accent', controls: 'The hover outline, and the underline of the inline variant.' },
    { token: '--caos-accent-wash', controls: 'The tint that fills the control while it has focus, in place of a focus ring.' },
    { token: '--caos-color-danger', controls: 'The required asterisk, and the outline and message when invalid.' },
  ],

  parts: [
    { part: 'label', is: 'the label above the control' },
    { part: 'input', is: 'the inner input element' },
    { part: 'message', is: 'the line under the control' },
  ],

  methods: [
    {
      name: 'focus',
      description:
        'Focus the inner control and select what is already in it, so typing replaces rather than appends. The ' +
        'selection is attempted and swallowed on the input types that do not support it, which is how one method ' +
        'serves all three subclasses.',
      args: [
        {
          name: 'options',
          type: 'FocusOptions',
          description: 'The ordinary focus options — pass `{ preventScroll: true }` to focus without scrolling to it.',
        },
      ],
    },
  ],

  placing:
    'It is not placed. Place one of the subclasses — TextField, NumberField or DateField — or FieldRenderer, ' +
    'which chooses between them from the metadata. What this entry is for is reading the contract once instead ' +
    'of three times, and knowing that a change to it moves all three.',

  whenToUse:
    'Read it when you want the shared contract in one place, or when you are adding another single-line input to ' +
    'the library — a new one is a subclass passing its input type to `super()`, and should be nothing more.',

  insteadUse: [
    { name: 'TextField', when: 'You want the shared field with a text input in it.' },
    { name: 'NumberField', when: 'You want it with a number input, tabular figures, and min/max/step that mean something.' },
    { name: 'DateField', when: 'You want it with a date input and the platform calendar.' },
    { name: 'FieldRenderer', when: 'The type is not known until the record is read.' },
    { name: 'CheckboxField', when: 'The value is a yes-or-no — it is a separate implementation, not one of these.' },
    { name: 'PicklistField', when: 'The value is one of a fixed list — also a separate implementation.' },
  ],

  accessibility:
    'One generated id per instance joins the label to the control, which is the whole reason it is generated: ' +
    'without it a screen reader announces an unnamed text box and clicking the label does nothing. `required` and ' +
    '`invalid` are forwarded to the input as `required` and `aria-invalid`, and the asterisk beside the label is ' +
    'hidden from assistive technology because the input already reports the same fact. Focus is drawn as a wash ' +
    'inside the control rather than as an outline ring. What is left to the author, in every subclass, is the ' +
    'label text and the wording of the message.',

  examples: [
    {
      key: 'the-three',
      label: 'The three primitives that are it',
      summary: 'The same template three times over — one label, one box, one message — differing only by the input type each passes up.',
      placement: [
        { component: 'caos-text-field', inputs: { label: 'Project name', value: 'North region rollout' } },
        { component: 'caos-number-field', inputs: { label: 'Hours', value: 48, step: 0.25 } },
        { component: 'caos-date-field', inputs: { label: 'Due date', value: '2026-09-15' } },
      ],
    },
    {
      key: 'shared-states',
      label: 'The states they share',
      summary:
        'Required, refused and locked, one on each subclass — the asterisk, the red outline with its reason, and ' +
        'the greyed control all come from the shared implementation rather than from any of the three.',
      placement: [
        { component: 'caos-text-field', inputs: { label: 'Project name', required: true, value: 'North region rollout' } },
        {
          component: 'caos-number-field',
          inputs: { label: 'Hours', value: -6, min: 0, invalid: true, message: 'Hours cannot be negative.' },
        },
        { component: 'caos-date-field', inputs: { label: 'Opened', value: '2026-08-04', disabled: true } },
      ],
    },
    {
      key: 'inline',
      label: 'The record-detail variant',
      summary:
        'The same three with `inline` set: the box is gone, an accent underline is left, and the type is inherited ' +
        'from the surrounding text — which is why the label and the message are hidden here and the record detail supplies them.',
      placement: [
        { component: 'caos-text-field', inputs: { inline: true, value: 'North region rollout' } },
        { component: 'caos-number-field', inputs: { inline: true, value: 48, step: 0.25 } },
        { component: 'caos-date-field', inputs: { inline: true, value: '2026-09-15' } },
      ],
    },
  ],
};

export default docs;
